/**
 * The project pulse: the six computed signals and their overrides.
 */

import { request } from './client.js';

// ----------------------------------------------------------------------- pulse

/** `unknown` is deliberately NOT green — it means nobody has measured it. */
export type SignalStatus = 'green' | 'amber' | 'red' | 'unknown';

export type SignalKey =
  | 'schedule'
  | 'approvals'
  | 'procurement'
  | 'site'
  | 'quality'
  | 'commercial';

/** A record that produced the status — `06`: "as links, not descriptions". */
export interface PulseDriver {
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly reference: string;
  readonly why: string;
}

export interface PulseOverride {
  readonly id: string;
  readonly signal: string;
  readonly target_status: SignalStatus;
  readonly reason: string;
  readonly expires_at: string;
  readonly created_by_name: string | null;
}

export interface PulseSignal {
  readonly key: SignalKey;
  readonly status: SignalStatus;
  /** What the rules said before any override. Never hidden. */
  readonly computed_status: SignalStatus;
  readonly drivers: readonly PulseDriver[];
  readonly not_computable?: string;
  readonly override: PulseOverride | null;
}

export interface Pulse {
  readonly overall: SignalStatus;
  readonly computed_overall: SignalStatus;
  readonly signals: readonly PulseSignal[];
  readonly measured: number;
  readonly unmeasured: readonly SignalKey[];
  readonly override: PulseOverride | null;
  readonly computed_at: string;
  readonly previous: { readonly overall: SignalStatus; readonly at: string } | null;
}

const pulsePath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/pulse`;

export function getPulse(projectId: string): Promise<Pulse> {
  return request<Pulse>(pulsePath(projectId));
}

export function overridePulse(
  projectId: string,
  input: { signal: string; status: 'green' | 'amber'; reason: string; expires_on: string },
): Promise<Pulse> {
  return request<Pulse>(`${pulsePath(projectId)}/override`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function clearPulseOverride(projectId: string, signal: string): Promise<Pulse> {
  return request<Pulse>(`${pulsePath(projectId)}/override/${encodeURIComponent(signal)}`, {
    method: 'DELETE',
  });
}
